/**
 * The Title Sheet, on screen as it is in the workbook.
 *
 * Companies keep these registers in a spreadsheet, and the book they hand over
 * opens on a contents page: a banner, the company's particulars, then the
 * eleven registers with their chapter, section and rule, each form number a
 * link to its sheet. Rendering the index as anything else meant the screen and
 * the download were two different documents, and the person checking one
 * against the other had to translate between them.
 *
 * So this is a sheet: column letters across the top, numbered rows down the
 * side, ruled cells, merged spans — and the footnote the source book carries,
 * "Please click on 'Form Name' to view the respective form", is literally true
 * here. The row numbers are the rows you see, so a reference to "row 12" means
 * the same thing on both.
 *
 * It is a reading surface, not an editable grid. Editing happens inside a
 * register, where the cells are the prescribed columns; the one action here is
 * opening one.
 */

import { TbUsers, TbCloudDownload } from 'react-icons/tb';

/* ── Sheet chrome ────────────────────────────────────────────────────────── */

/** Content columns B…G, matching the workbook's Title Sheet. */
const COLUMNS = [
  { letter: 'B', width: 46 },
  { letter: 'C', width: 104 },
  { letter: 'D', width: 340 },
  { letter: 'E', width: 84 },
  { letter: 'F', width: 380 },
  { letter: 'G', width: 96 },
];

const ROW_HEADER_WIDTH = 34;

const CELL = 'border border-[#ddd6c6] px-2 py-[7px] text-[12px] text-[#08111f] align-middle leading-snug';
const HEAD = 'border border-[#ddd6c6] bg-[#f4f1e9] text-[10px] font-bold text-[#8b8371] text-center py-1 select-none';

/** The tan banner and the grey header band of the source book. */
const BANNER = 'bg-[#f7e0c8] text-center font-bold text-[12px] tracking-[0.3px]';
const BAND = 'bg-[#e6e1d4] text-[11px] font-bold uppercase tracking-[0.4px] text-center';

/** A row of the sheet: its number in the gutter, then whatever cells it holds. */
function Row({ n, children, onClick, clickable }) {
  return (
    <tr
      className={clickable ? 'cursor-pointer hover:bg-[#f7f5ef]' : undefined}
      onClick={onClick}
    >
      <th scope="row" className={`${HEAD} sticky left-0 z-10 tabular-nums font-normal`}>{n}</th>
      {children}
    </tr>
  );
}

/** An empty ruled row — the blank lines the sheet uses to separate its blocks. */
const Spacer = ({ n }) => (
  <Row n={n}>
    <td className={CELL} colSpan={COLUMNS.length}>&nbsp;</td>
  </Row>
);

/* ── The sheet ───────────────────────────────────────────────────────────── */

export default function RegisterTitleSheet({ company, items, onOpen }) {
  // Row numbers are assigned as the sheet is built rather than hard-coded, so
  // adding a line above the table cannot leave the gutter lying.
  let row = 0;
  const next = () => { row += 1; return row; };

  const banner = next();
  const afterBanner = next();
  const nameRow = next();
  const cinRow = next();
  const officeRow = next();
  const afterCompany = next();
  const headRow = next();
  const registerRows = items.map((item) => ({ item, n: next() }));
  const afterTable = next();
  const abbrRow = next();
  const noteRow = next();
  const lastRow = next();

  const label = (text) => (
    <td className={`${CELL} font-bold whitespace-nowrap`} colSpan={2}>{text}</td>
  );

  return (
    <div className="rounded-xl border-[1.5px] border-[#ddd6c6] bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="border-collapse" style={{ minWidth: '100%' }}>
          <colgroup>
            <col style={{ width: ROW_HEADER_WIDTH }} />
            {COLUMNS.map((c) => <col key={c.letter} style={{ width: c.width }} />)}
          </colgroup>

          <thead>
            <tr>
              <th className={`${HEAD} sticky left-0 z-20`} />
              {COLUMNS.map((c) => <th key={c.letter} className={HEAD}>{c.letter}</th>)}
            </tr>
          </thead>

          <tbody>
            <Row n={banner}>
              <td className={`${CELL} ${BANNER}`} colSpan={COLUMNS.length}>
                REGISTERS UNDER THE COMPANIES ACT, 2013
              </td>
            </Row>
            <Spacer n={afterBanner} />

            <Row n={nameRow}>
              {label('Name of the company:')}
              <td className={`${CELL} font-bold`} colSpan={4}>{company?.name || '—'}</td>
            </Row>
            <Row n={cinRow}>
              {label('CIN:')}
              <td className={`${CELL} font-bold tabular-nums`} colSpan={4}>{company?.cin || '—'}</td>
            </Row>
            <Row n={officeRow}>
              {label('Registered office address:')}
              <td className={`${CELL} font-bold`} colSpan={4}>{company?.registeredAddress || '—'}</td>
            </Row>
            <Spacer n={afterCompany} />

            <Row n={headRow}>
              <td className={`${CELL} ${BAND}`}>#</td>
              <td className={`${CELL} ${BAND}`}>Form name</td>
              <td className={`${CELL} ${BAND}`}>Name of register</td>
              <td className={`${CELL} ${BAND}`}>Chapter</td>
              <td className={`${CELL} ${BAND}`}>Relevant section &amp; rule</td>
              <td className={`${CELL} ${BAND}`}>Entries</td>
            </Row>

            {registerRows.map(({ item, n }) => (
              <Row key={item.id} n={n} clickable onClick={() => onOpen(item)}>
                <td className={`${CELL} text-center tabular-nums font-bold`}>{item.serial}</td>
                <td className={`${CELL} text-center`}>
                  {/* The form number is the link, exactly as the note at the
                      foot of the sheet says. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpen(item); }}
                    className="font-bold text-[#0b5cad] underline underline-offset-2 hover:text-[#08111f]"
                  >
                    {item.form}
                  </button>
                </td>
                <td className={CELL}>
                  {item.title}
                  {(item.layout === 'folio' || item.autofillSource || item.books.length > 1) && (
                    <span className="ml-1.5 inline-flex items-center gap-1.5 align-middle">
                      {item.layout === 'folio' && (
                        <span className="text-[10px] text-[#8b8371] whitespace-nowrap">
                          <TbUsers size={11} className="inline -mt-0.5 mr-0.5" />
                          per {item.id === 'dir-kmp' ? 'director' : 'holder'}
                        </span>
                      )}
                      {item.autofillSource && (
                        <span className="text-[10px] text-[#4a6b3f] whitespace-nowrap">
                          <TbCloudDownload size={11} className="inline -mt-0.5 mr-0.5" />
                          fills from company data
                        </span>
                      )}
                      {item.books.length > 1 && (
                        <span className="text-[10px] text-[#8b8371] whitespace-nowrap">
                          ({item.books.map((b) => b.seriesKey || '—').join(', ')})
                        </span>
                      )}
                    </span>
                  )}
                </td>
                <td className={`${CELL} text-center`}>{item.chapter}</td>
                <td className={`${CELL} text-[11px] text-[#5c5445]`}>
                  {item.section} &amp; {item.rule}
                </td>
                <td className={`${CELL} text-center tabular-nums`}>
                  {item.rowCount > 0
                    ? <span className="font-bold">{item.rowCount}</span>
                    : <span className="text-[#a39a86]">Nil</span>}
                </td>
              </Row>
            ))}

            <Spacer n={afterTable} />

            <Row n={abbrRow}>
              <td className={`${CELL} font-bold`} colSpan={2}>Abbreviations:</td>
              <td className={CELL}>&nbsp;</td>
              <td className={`${CELL} font-bold`} colSpan={3}>Note:</td>
            </Row>
            <Row n={noteRow}>
              <td className={CELL} colSpan={2}>S = Section</td>
              <td className={CELL}>COS = Companies</td>
              <td className={CELL} colSpan={3}>
                Please click on ‘Form name’ to view the respective form
              </td>
            </Row>
            <Row n={lastRow}>
              <td className={CELL} colSpan={2}>R = Rules</td>
              <td className={CELL} colSpan={4}>&nbsp;</td>
            </Row>
          </tbody>
        </table>
      </div>
    </div>
  );
}
